import { useLayoutEffect, useRef, useState } from 'react'

const CHIP_LEFT = [35.52, 156.52, 277.52, 410.52, 531.52]

const BUBBLE_SPOT = [
  { left: 249.52, top: 28.52 },
  { left: 443.52, top: 71.52 },
]

const CIRCLE = { left: 35.52, top: 28.52, width: 167, height: 86 }
const CIRCLE_CX = CIRCLE.left + CIRCLE.width / 2
const CIRCLE_CY = CIRCLE.top + CIRCLE.height / 2
const CIRCLE_RIGHT = CIRCLE.left + CIRCLE.width
const CIRCLE_BOTTOM = CIRCLE.top + CIRCLE.height
const TRUNK_Y = 148.02
const CHIP_TOP = 181.52
const BUBBLE_JOINT_X = 226.02
const BUBBLE_HALF_H = 23
const CORNER = 8
const LINE_COLOR = '#D3D6E8'

function chipBranchPath(centers: number[]): string {
  if (centers.length === 0) return ''
  const first = centers[0] as number
  const last = centers[centers.length - 1] as number
  if (centers.length === 1) return `M ${first} ${CIRCLE_BOTTOM} V ${CHIP_TOP}`

  const parts = [
    `M ${CIRCLE_CX} ${CIRCLE_BOTTOM} V ${TRUNK_Y}`,
    `M ${first + CORNER} ${TRUNK_Y} H ${last - CORNER}`,
    `M ${first} ${CHIP_TOP} V ${TRUNK_Y + CORNER} Q ${first} ${TRUNK_Y} ${first + CORNER} ${TRUNK_Y}`,
    `M ${last - CORNER} ${TRUNK_Y} Q ${last} ${TRUNK_Y} ${last} ${TRUNK_Y + CORNER} V ${CHIP_TOP}`,
  ]
  for (const cx of centers.slice(1, -1)) parts.push(`M ${cx} ${TRUNK_Y} V ${CHIP_TOP}`)
  return parts.join(' ')
}

function bubbleBranchPath(count: number): string {
  if (count === 0) return ''
  const spots = BUBBLE_SPOT.slice(0, count)
  const parts = [`M ${CIRCLE_RIGHT} ${CIRCLE_CY} H ${BUBBLE_JOINT_X}`]
  for (const spot of spots) {
    const cy = spot.top + BUBBLE_HALF_H
    if (Math.abs(cy - CIRCLE_CY) < 1) {
      parts.push(`M ${BUBBLE_JOINT_X} ${cy} H ${spot.left}`)
      continue
    }
    const up = cy < CIRCLE_CY
    const turn = up ? cy + CORNER : cy - CORNER
    parts.push(
      `M ${BUBBLE_JOINT_X} ${CIRCLE_CY} V ${turn}` +
        ` Q ${BUBBLE_JOINT_X} ${cy} ${BUBBLE_JOINT_X + CORNER} ${cy}` +
        ` H ${spot.left}`,
    )
  }
  return parts.join(' ')
}

export type KeywordMapRank = {
  title: string
  fitGradeLabel: string | null
}

export function AiKeywordMap({
  title,
  keywords,
  ranks,
}: {
  title: string
  keywords: string[]
  ranks: KeywordMapRank[]
}) {
  const words = keywords.slice(0, CHIP_LEFT.length)
  const shown = ranks.slice(0, BUBBLE_SPOT.length)

  const chipRefs = useRef<(HTMLSpanElement | null)[]>([])
  const [chipCenters, setChipCenters] = useState<number[]>([])

  useLayoutEffect(() => {
    const measure = () => {
      const centers = chipRefs.current
        .slice(0, words.length)
        .flatMap((el) => (el === null ? [] : [el.offsetLeft + el.offsetWidth / 2]))
      setChipCenters((prev) =>
        prev.length === centers.length && prev.every((v, i) => v === centers[i]) ? prev : centers,
      )
    }
    measure()
    const observer = new ResizeObserver(measure)
    for (const el of chipRefs.current) if (el !== null) observer.observe(el)
    return () => observer.disconnect()
  }, [words.length])

  return (
    <div className="relative h-[230px] w-[640px] shrink-0 overflow-clip rounded-xl border-[0.483px] border-white-400 bg-white-50">
      <svg
        aria-hidden="true"
        className="absolute inset-0"
        width="640"
        height="230"
        viewBox="0 0 640 230"
        fill="none"
      >
        <path d={chipBranchPath(chipCenters)} stroke={LINE_COLOR} />
        <path d={bubbleBranchPath(shown.length)} stroke={LINE_COLOR} />
      </svg>

      <div className="absolute left-[531.52px] top-[13.52px] flex items-center gap-2">
        <img alt="" aria-hidden="true" className="h-[15px] w-[15px]" src="/icon/keyword-map/label-dot.svg" />
        <span className="text-caption font-medium whitespace-nowrap text-[#10168a]">AI 추출 키워드</span>
      </div>

      <div
        className="absolute left-[35.52px] top-[28.52px] h-[86px] w-[167px] overflow-clip rounded-[999px] shadow-[8px_13px_30px_0_rgba(156,151,171,0.6)]"
        style={{
          backgroundImage:
            'linear-gradient(156.6345deg, rgb(116,55,216) 7.2508%, rgb(61,29,114) 88.414%)',
        }}
      >
        <div className="absolute left-[46px] top-[13px] flex w-[76px] flex-col items-center gap-[5px]">
          <span className="inline-flex items-center justify-center rounded-[19px] bg-violet-100 px-[7px] py-[3px] text-[11px] leading-[1.2] font-medium whitespace-nowrap text-violet-600">
            시민문제
          </span>
          <span className="line-clamp-2 w-full text-center text-body-sm font-semibold text-white-50">
            {title}
          </span>
        </div>
      </div>

      {shown.map((rank, i) => {
        const spot = BUBBLE_SPOT[i] as (typeof BUBBLE_SPOT)[number]
        return (
          <div
            key={spot.left}
            className="absolute h-[46px] w-[161px] overflow-clip rounded-[99px] border border-white-200 bg-[rgb(255_255_255/93%)] shadow-[8px_10px_12px_0_rgba(156,151,171,0.09)]"
            style={{ left: spot.left, top: spot.top }}
          >
            <div className="absolute left-[5px] top-[5px] flex w-[145.82px] items-center justify-between">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[25px] bg-violet-600 text-label font-semibold text-white-50">
                {i + 1}위
              </span>
              <span className="flex w-[105px] shrink-0 flex-col items-center justify-center gap-1 text-center whitespace-nowrap">
                <span className="w-full truncate text-label font-medium text-black-200">
                  {rank.title}
                </span>
                <span className="w-full truncate text-caption font-medium text-violet-800">
                  적합도 {rank.fitGradeLabel ?? '확인 필요'}
                </span>
              </span>
            </div>
          </div>
        )
      })}

      {words.map((word, i) => {
        const left = CHIP_LEFT[i] as number
        return (
          <span
            key={left}
            ref={(el) => {
              chipRefs.current[i] = el
            }}
            className="absolute top-[181.52px] inline-flex items-center justify-center rounded-lg border border-[rgb(0_0_0/5%)] bg-[#edefff] px-3 py-1 text-body-sm font-medium whitespace-nowrap text-[#10168a] shadow-[3px_2px_6px_rgba(0,0,0,0.05),3px_1px_6.5px_rgba(0,0,0,0.02)]"
            style={{ left }}
          >
            {word}
          </span>
        )
      })}
    </div>
  )
}
